/**
 * useJobMonitor Hook
 * Handles job status monitoring
 */
import { useState, useCallback, useMemo, useRef } from "react";
import { useCspApi } from "@perfectserve/http-ps";
import { createProvisioningService } from "../services/provisioningService";
/**
 * Hook for monitoring provisioning job status
 */
export function useJobMonitor() {
    const { getAuthToken } = useCspApi();
    const [status, setStatus] = useState("");
    const [isMonitoring, setIsMonitoring] = useState(false);
    const [createdUsers, setCreatedUsers] = useState([]);
    const [successfullyCreatedCount, setSuccessfullyCreatedCount] = useState(0);
    const [hasWarnings, setHasWarnings] = useState(false);
    const [error, setError] = useState(null);
    const stopRef = useRef(false);
    // Create provisioning service - uses env-ps for configuration
    const provisioningService = useMemo(() => createProvisioningService(getAuthToken), [getAuthToken]);
    const startMonitoring = useCallback(async (jobId) => {
        setIsMonitoring(true);
        setStatus("Monitoring job...");
        setCreatedUsers([]);
        setSuccessfullyCreatedCount(0);
        setHasWarnings(false);
        setError(null);
        stopRef.current = false;
        try {
            const result = await provisioningService.monitorJobStatus(jobId, (jobStatus, users) => {
                if (stopRef.current)
                    return;
                setStatus(jobStatus);
                setCreatedUsers(users);
            });
            setSuccessfullyCreatedCount(result.successfullyCreatedCount);
            setHasWarnings(result.hasWarnings);
            if (!result.success && result.successfullyCreatedCount === 0) {
                if (result.hasWarnings) {
                    setError("No users were created - see warnings below");
                }
                else {
                    setError("Job did not complete successfully");
                }
            }
            return result;
        }
        catch (err) {
            const errorMessage = err instanceof Error ? err.message : "Monitoring failed";
            setError(errorMessage);
            return { success: false, createdUsers: [], successfullyCreatedCount: 0, hasWarnings: false };
        }
        finally {
            setIsMonitoring(false);
        }
    }, [provisioningService]);
    const stopMonitoring = useCallback(() => {
        stopRef.current = true;
        setIsMonitoring(false);
    }, []);
    const clear = useCallback(() => {
        stopRef.current = true;
        setStatus("");
        setIsMonitoring(false);
        setCreatedUsers([]);
        setSuccessfullyCreatedCount(0);
        setHasWarnings(false);
        setError(null);
    }, []);
    return {
        status,
        isMonitoring,
        createdUsers,
        successfullyCreatedCount,
        hasWarnings,
        error,
        startMonitoring,
        stopMonitoring,
        clear,
    };
}
